<template>
  <div class="row list-filter-row max-pos-filterbar" data-max-host role="search">
    <div
      v-for="(filter, i) in visibleFilters"
      :key="i"
      class="col-md-3 col-sm-6 filtercol"
    >
      <div class="form-group">
        <div v-if="filter.input === 'search'" class="input-group">
          <input
            class="form-control"
            type="text"
            :value="filter.value ?? ''"
            :placeholder="filter.placeholder || filter.label"
            :aria-label="filter.label"
            v-on:input="typed(i, filter, $event.target.value)"
            v-on:keydown.enter.prevent="commit(i, filter, $event.target.value)"
          />
          <span class="input-group-btn">
            <button
              v-if="hasExtra"
              type="button"
              :class="'btn btn-info hidden-col-sm hidden-col-md hidden-col-lg' + (expanded ? ' active' : '')"
              :title="expanded ? _('Hide filters') : _('Show filters')"
              v-on:click="expanded = !expanded"
            >
              <i :class="'fa fa-fw ' + (expanded ? 'fa-caret-up' : 'fa-sliders')" aria-hidden="true"></i>
            </button>
            <button type="button" class="btn btn-success" :aria-label="filter.label" v-on:click="commit(i, filter, filter.value ?? '')">
              <i class="fa fa-fw fa-search" aria-hidden="true"></i>
            </button>
          </span>
        </div>
        <div v-else-if="filter.input === 'date'">
          <div class="input-group">
            <div :id="baseId + '-' + i" class="input-group-addon">{{ filter.label }}</div>
            <DatePicker :modelValue="filter.value || null" :shortLocale="shortLocale" :staticInput="true" :placeholder="filter.placeholder || filter.label" v-on:update:modelValue="commit(i, filter, toIso($event))" />
          </div>
        </div>
        <div v-else-if="filter.input === 'datetime'">
          <div class="input-group">
            <div :id="baseId + '-' + i" class="input-group-addon">{{ filter.label }}</div>
            <input class="form-control" type="datetime-local" :aria-labelledby="baseId + '-' + i" :value="toLocalDateTime(filter.value)" v-on:change="commit(i, filter, fromLocalDateTime($event.target.value))" />
          </div>
        </div>
        <div v-else-if="filter.input === 'time'">
          <div class="input-group">
            <div :id="baseId + '-' + i" class="input-group-addon">{{ filter.label }}</div>
            <input class="form-control" type="time" :aria-labelledby="baseId + '-' + i" :value="filter.value || ''" :step="timeStep(filter)" v-on:change="commitTime(i, filter, $event.target.value)" />
          </div>
        </div>
        <div v-else-if="filter.input === 'select' || filter.input === 'multiselect'">
          <MaxPosPicker
            :label="filter.label"
            :options="filter.options"
            :modelValue="filter.value"
            :multiple="filter.input === 'multiselect'"
            :placeholder="filter.placeholder || filter.label"
            :optionsState="filter.optionsState"
            v-on:update:modelValue="commit(i, filter, $event)"
          />
        </div>
        <div v-else-if="filter.input === 'daterange'">
          <div class="input-group">
            <div :id="baseId + '-' + i" class="input-group-addon">{{ filter.label }}</div>
            <select class="form-control" :aria-labelledby="baseId + '-' + i" :value="filter.periodValue || ''" v-on:change="setPeriod(i, filter, $event.target.value)">
              <option value="">{{ _('Select date range') }}</option>
              <option v-for="preset in periodPresets" :key="preset.value" :value="preset.value">{{ preset.label }}</option>
              <option value="custom">{{ _('Select exact date range') }}</option>
            </select>
          </div>
          <div v-if="filter.periodValue === 'custom' || showExact(filter)" class="max-pos-filterbar__dates">
            <DatePicker :modelValue="filter.fromValue || null" :shortLocale="shortLocale" :staticInput="true" :placeholder="_('Select start date')" v-on:update:modelValue="setDate(i, filter, 'from', $event)" />
            <span aria-hidden="true">–</span>
            <DatePicker :modelValue="filter.toValue || null" :shortLocale="shortLocale" :staticInput="true" :placeholder="_('Select end date')" v-on:update:modelValue="setDate(i, filter, 'to', $event)" />
          </div>
        </div>
        <div v-else class="input-group">
          <div :id="baseId + '-' + i" class="input-group-addon">{{ filter.label }}</div>
          <input class="form-control" :aria-labelledby="baseId + '-' + i" :value="filter.value ?? ''" :placeholder="filter.placeholder" v-on:change="commit(i, filter, $event.target.value)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useId } from 'vue'
import { DateTime } from 'luxon'
import { useMaxInterface } from '#max-runtime/context.ts'
import { daterangePresetRange, normalizeTime } from '#max-core'
import DatePicker from '~/components/DatePicker.vue'
import MaxPosPicker from '~/components/MaxPosPicker.vue'

const LIVE_DELAY_MS = 300

function filterbarTimezone (store) {
  return (store && store.getters && store.getters.timezone) || 'Europe/Helsinki'
}

// Same preset dates the runtime fills before on.load (johku-max daterangePresetRange)
function presetRange (period, timezone) {
  if (!period || period === 'all' || period === 'custom') return { from: '', to: '' }
  return daterangePresetRange(period, DateTime.now().setZone(timezone || 'Europe/Helsinki').toISODate())
}

function hydrateDaterangeFilters (node, iface, timezone) {
  if (!iface) return
  for (const filter of (node && node.filters) || []) {
    if (filter.input !== 'daterange' || !filter.period) continue
    const period = filter.periodValue || ''
    if (!period || period === 'custom' || period === 'all') continue
    if (filter.fromValue && filter.toValue) continue
    const range = presetRange(period, timezone)
    if (!range.from || !range.to) continue
    if (filter.from) iface.setState(filter.from, range.from)
    if (filter.to) iface.setState(filter.to, range.to)
  }
}

export default {
  components: { DatePicker, MaxPosPicker },
  props: ['node'],
  setup () {
    const { $translate } = useNuxtApp()
    return { iface: useMaxInterface(), baseId: 'max-pos-filter-' + useId(), _: $translate }
  },
  data () {
    return { expanded: false }
  },
  created () {
    this.timers = {}
    this.committed = {}
    hydrateDaterangeFilters(this.node, this.iface, filterbarTimezone(this.$store))
  },
  mounted () {
    hydrateDaterangeFilters(this.node, this.iface, filterbarTimezone(this.$store))
  },
  beforeUnmount () {
    Object.values(this.timers).forEach(clearTimeout)
  },
  computed: {
    shortLocale () {
      return (this.$store && this.$store.getters.locale ? this.$store.getters.locale : 'fi_FI').split('_')[0]
    },
    hasExtra () {
      return (this.node.filters || []).length > 1
    },
    visibleFilters () {
      const filters = this.node.filters || []
      if (!this.hasExtra || this.expanded || typeof window === 'undefined' || window.innerWidth >= 768) {
        return filters
      }
      return filters.slice(0, 1)
    },
    periodPresets () {
      return [
        { value: 'all', label: this._('All') },
        { value: 'today', label: this._('Today') },
        { value: 'yesterday', label: this._('Yesterday') },
        { value: 'overview', label: this._('Overview') },
        { value: 'lastweek', label: this._('Last week') },
        { value: 'lastmonth', label: this._('Last month') },
        { value: 'thisweek', label: this._('This week') },
        { value: 'thismonth', label: this._('This month') },
        { value: 'thisyear', label: this._('This year') },
        { value: 'startingtoday', label: this._('Starting today') },
        { value: 'starting', label: this._('All upcoming') }
      ]
    }
  },
  methods: {
    showExact (filter) {
      return !!(filter.fromValue || filter.toValue) && !filter.periodValue
    },
    typed (i, filter, value) {
      this.iface.setState(filter.state, value)
      clearTimeout(this.timers[i])
      this.timers[i] = setTimeout(() => this.commit(i, filter, value), LIVE_DELAY_MS)
    },
    /**
     * The value as the tool sees it: the live state when the runtime offers it (two emits in one
     * tick see the first one's write), else the value the filter was drawn with
     */
    current (name, drawn) {
      if (!name) return drawn
      return this.iface.getState ? this.iface.getState(name) : drawn
    },
    /** Comparable form of a filter value: '' when empty, lists by content, dates as ISO */
    normalized (filter, value) {
      if (value === null || value === undefined || value === '') return ''
      if (Array.isArray(value)) {
        return value.length ? JSON.stringify(value.map(v => String(v)).sort()) : ''
      }
      if (filter.input === 'date' || filter.input === 'daterange') return this.toIso(value)
      if (filter.input === 'datetime') {
        const dt = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(String(value), { zone: this.timezone() })
        return dt.isValid ? String(dt.toMillis()) : String(value)
      }
      return typeof value === 'object' ? JSON.stringify(value) : String(value)
    },
    /**
     * Writes the filter's state and runs its on.change, only when the value changed (JDEV-3307):
     * pickers can report the value on.load wrote, and an on.change with <refresh/> would loop.
     * Choosing the value already shown does nothing either, as the tool already shows its data.
     * A search runs on Enter and the search button even when unchanged since the last keystroke,
     * but not twice for the same text.
     */
    commit (i, filter, value) {
      clearTimeout(this.timers[i])
      if (filter.input !== 'search' && filter.state && this.normalized(filter, value) === this.normalized(filter, this.current(filter.state, filter.value))) return
      if (filter.state) this.iface.setState(filter.state, value)
      if (filter.input === 'search' && this.committed[i] === value) return
      this.committed[i] = value
      const change = filter.on && filter.on.change
      if (change) this.iface.run(change, { ...(this.node.scope || {}), event: { value } })
    },
    /** The spec gives the step in minutes, the time input takes seconds */
    timeStep (filter) {
      return typeof filter.step === 'number' && filter.step >= 1 ? Math.round(filter.step) * 60 : 60
    },
    /** Stores HH:MM ('' when cleared); an incomplete time reads as '' too, so nothing reruns until it changes */
    commitTime (i, filter, value) {
      const time = normalizeTime(value)
      if (time === (filter.value || '')) return
      this.commit(i, filter, time)
    },
    timezone () {
      return filterbarTimezone(this.$store)
    },
    setPeriod (i, filter, value) {
      const range = value !== 'custom' ? presetRange(value, this.timezone()) : null
      // Same period (and for a preset the same dates): nothing to write or rerun
      if (filter.period && (value || '') === (this.current(filter.period, filter.periodValue) || '') &&
        (!range || ((!filter.from || range.from === this.normalized(filter, this.current(filter.from, filter.fromValue))) &&
          (!filter.to || range.to === this.normalized(filter, this.current(filter.to, filter.toValue)))))) return
      if (filter.period) this.iface.setState(filter.period, value)
      if (range) {
        if (filter.from) this.iface.setState(filter.from, range.from)
        if (filter.to) this.iface.setState(filter.to, range.to)
      } else if ((filter.from && !filter.fromValue) || (filter.to && !filter.toValue)) {
        return
      }
      this.commit(i, filter, value)
    },
    setDate (i, filter, which, value) {
      const iso = this.toIso(value)
      // The picker can report the date it was given (a preset's dates); that is no change and
      // must not turn the preset into custom dates
      if (filter[which] && iso === this.normalized(filter, this.current(filter[which], filter[which + 'Value']))) return
      if (filter[which]) this.iface.setState(filter[which], iso)
      if (filter.period) this.iface.setState(filter.period, 'custom')
      const from = which === 'from' ? iso : (this.current(filter.from, filter.fromValue) || '')
      const to = which === 'to' ? iso : (this.current(filter.to, filter.toValue) || '')
      if ((filter.from && !from) || (filter.to && !to)) return
      this.commit(i, filter, iso)
    },
    toIso (value) {
      if (!value) return ''
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
      const dt = value instanceof Date
        ? DateTime.fromJSDate(value, { zone: this.timezone() })
        : DateTime.fromISO(String(value), { zone: this.timezone() })
      return dt.isValid ? dt.toISODate() : ''
    },
    presetRange (period) {
      return presetRange(period, this.timezone())
    },
    toLocalDateTime (value) {
      if (!value) return ''
      const dt = DateTime.fromISO(String(value), { zone: this.timezone() })
      return dt.isValid ? dt.toFormat("yyyy-LL-dd'T'HH:mm") : ''
    },
    fromLocalDateTime (value) {
      if (!value) return ''
      const dt = DateTime.fromISO(String(value), { zone: this.timezone() })
      return dt.isValid ? dt.toISO({ suppressMilliseconds: true }) : ''
    }
  }
}
</script>

<style scoped>
.max-pos-filterbar .form-group {
  margin-bottom: 0;
}
.max-pos-filterbar__dates {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
</style>
